import { Reducer } from "redux";
import * as Sentry from "@sentry/browser";
import { camelizeKeys } from "humps";
import { CALL_API } from "../helpers/middleware/api";
import createRequestTypes from "../helpers/createRequestTypes";
import { HooplaUser } from "types";
import { genericPush } from "../gtm";

const COOKIE_USER = createRequestTypes("COOKIE_USER");
export const fetchCookieUser = () => ({
  [CALL_API]: {
    types: COOKIE_USER,
    endpoint: "/api/v2.0/users"
  }
});

const USER = createRequestTypes("USER");
export const fetchUser = (userId: number) => ({
  [CALL_API]: {
    types: USER,
    endpoint: `/api/v2.0/users/${userId}`
  }
});

export const createDirtyUser = () => ({ type: "CREATE_DIRTY_USER" });
export const changeUserField = (key: string, value: string) => ({
  type: "CHANGE_USER_FIELD",
  key,
  value
});

const PATCH_USER = createRequestTypes("PATCH_USER");
export const saveUser = (
  userId: number,
  firstName: string,
  lastName: string,
  phoneNumber: string
) => ({
  [CALL_API]: {
    types: PATCH_USER,
    endpoint: `/api/v2.0/users/${userId}`,
    options: {
      method: "PATCH",
      body: JSON.stringify({
        json_patch: {
          patches: [
            { op: "replace", path: "/first_name", value: firstName || "" },
            { op: "replace", path: "/last_name", value: lastName || "" },
            phoneNumber && {
              op: "replace",
              path: "/phone_number",
              value: phoneNumber || ""
            }
          ].filter(x => !!x)
        }
      })
    }
  }
});

export interface UserState {
  fetched: boolean;
  fetching: boolean;
  dirtyUserCreated: boolean;
  patching: boolean;
  userData?: HooplaUser;
  dirtyUser?: HooplaUser;
  error: any;
}

const initialState: UserState = {
  fetched: false,
  fetching: false,
  error: null,
  dirtyUserCreated: false,
  patching: false
};

const reducer: Reducer<UserState> = (
  state = initialState,
  action
): UserState => {
  switch (action.type) {
    case COOKIE_USER.REQUEST:
      return { ...state, fetched: false, fetching: true };

    case COOKIE_USER.SUCCESS: {
      Sentry.configureScope(scope => {
        scope.setUser({ id: action.response.data.user_id });
      });

      genericPush(action.response.data.analytics_user_id);
      const userData = camelizeKeys(action.response
        .data as object) as HooplaUser;
      return {
        ...state,
        fetched: true,
        fetching: false,
        userData,
        error: null
      };
    }

    case COOKIE_USER.FAILURE:
      return { ...state, fetched: true, fetching: false, error: action.error };

    case "CREATE_DIRTY_USER":
      return {
        ...state,
        dirtyUser: { ...state.userData },
        dirtyUserCreated: true
      };

    case "CHANGE_USER_FIELD": {
      const diff = { [action.key]: action.value };
      return { ...state, dirtyUser: { ...state.dirtyUser, ...diff } };
    }

    case PATCH_USER.REQUEST:
      return { ...state, patching: true, error: false };
    case PATCH_USER.SUCCESS:
      const userData = camelizeKeys(action.response
        .data as object) as HooplaUser;
      return { ...state, userData, patching: false };
    case PATCH_USER.FAILURE:
      return { ...state, patching: false, error: true };

    default:
      return state;
  }
};

export default reducer;
